import { computed, type MaybeRefOrGetter, toValue } from 'vue';

/**
 * A Vue composable for validating required props.
 *
 * @param props - The props object from the component, passed as a MaybeRefOrGetter.
 * @param requiredKeys - An array of keys that are required in the props.
 * @returns An object with reactive properties for validation.
 */
export function usePropValidator<T extends object>(
  props: MaybeRefOrGetter<T>,
  requiredKeys: (keyof T)[]
) {
  // Use toValue to ensure we are working with the raw object,
  // making it compatible with `props` from defineProps.
  const propsValue = computed(() => toValue(props));

  const missingParams = computed(() => {
    return requiredKeys.filter(key => !propsValue.value[key]);
  });

  const hasMandatoryParams = computed(() => {
    return missingParams.value.length === 0;
  });

  const validationErrorMessage = computed(() => {
    if (hasMandatoryParams.value) {
      return null;
    }
    if (missingParams.value.length === 1 && missingParams.value[0] === 'ids') {
      return "The 'ids' parameter must contain at least one valid article ID.";
    }
    const paramsList = missingParams.value.join(', ');
    return `Incomplete URL. The following parameters are required: ${paramsList}`;
  });

  return {
    hasMandatoryParams,
    validationErrorMessage,
    missingParams,
  };
}
